/**
 * DermaQuest Cart Drawer — dq-cart-drawer.js
 *
 * Everything lives inside #CartDrawer in the Liquid. Dawn replaces
 * #CartDrawer's innerHTML on every cart update — upsell, shipping bar,
 * and footer all re-render fresh from Liquid automatically.
 *
 * Responsibilities:
 *  - Upsell add-to-cart (optimistic UI, triggers Dawn render)
 *  - Quantity stepper validation (min/max enforcement before Dawn fires)
 *  - Expandable sections (re-bound after each Dawn render)
 *  - Discount code (re-bound after each Dawn render)
 *  - Remove empty-state class from root element after cart updates
 */

'use strict';

/* ─── Utilities ──────────────────────────────────────────────── */

const dqAll = (sel, root = document) => root.querySelectorAll(sel);

/* ─── Dawn section render ────────────────────────────────────── */

async function dqTriggerDawnRender() {
  const drawerEl = document.querySelector('cart-drawer');
  if (!drawerEl || typeof drawerEl.renderContents !== 'function') return;
  try {
    const res = await fetch('/cart?sections=cart-drawer');
    if (!res.ok) return;
    const sections = await res.json();
    drawerEl.renderContents({ sections });
  } catch (err) {
    console.error('[DQ Cart] Dawn render failed:', err);
  }
}

/* ─── Header cart count ──────────────────────────────────────── */

async function dqSyncCartCount() {
  try {
    const res = await fetch('/cart.js');
    if (!res.ok) return;
    const cart = await res.json();
    const bubble = document.querySelector('.dq-header__cart-count');
    if (!bubble) return;
    bubble.textContent   = cart.item_count;
    bubble.style.display = cart.item_count > 0 ? '' : 'none';
  } catch (_) {}
}

/* ─── Upsell ─────────────────────────────────────────────────── */

function dqInitUpsell() {
  dqAll('.dq-cart-upsell__add:not([data-dq-bound])').forEach((btn) => {
    btn.dataset.dqBound = '1';
    btn.addEventListener('click', dqHandleUpsellAdd);
  });
}

async function dqHandleUpsellAdd(e) {
  const btn       = e.currentTarget;
  const variantId = parseInt(btn.dataset.variantId, 10);
  if (!variantId) return;

  // Optimistic: remove card immediately so UI feels instant
  const card = btn.closest('.dq-cart-upsell__item');
  const grid = btn.closest('.dq-cart-upsell__grid');
  card?.remove();

  try {
    const res = await fetch('/cart/add.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body:    JSON.stringify({ id: variantId, quantity: 1 }),
    });

    if (res.status === 422) {
      if (card && grid) grid.appendChild(card);
      btn.textContent = 'Out of stock';
      btn.disabled    = true;
      return;
    }

    if (!res.ok) throw new Error(`/cart/add.js ${res.status}`);

    dqTriggerDawnRender();
  } catch (err) {
    console.error('[DQ Cart] Upsell add failed:', err);
    if (card && grid) grid.appendChild(card);
  }
}


/* ─── Quantity + remove via Cart API ─────────────────────────── */

const _drawerControllers = {};

async function dqDrawerUpdateQty(key, newQty) {
  if (_drawerControllers[key]) _drawerControllers[key].abort();
  const controller = new AbortController();
  _drawerControllers[key] = controller;

  // Optimistic: disable the item while request is in flight
  const itemEl = document.querySelector(`.dq-cart-item[data-index]`);
  const allBtns = document.querySelectorAll(`[data-key="${key}"]`);
  allBtns.forEach(el => el.setAttribute('disabled', ''));

  try {
    const res = await fetch('/cart/change.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ id: key, quantity: newQty }),
      signal:  controller.signal,
    });

    const cart = await res.json();
    delete _drawerControllers[key];

    if (cart.status) {
      dqDrawerShowError(key, cart.description || 'Could not update item.');
      allBtns.forEach(el => el.removeAttribute('disabled'));
      return;
    }

    // Let Dawn re-render the whole drawer — cleanest approach
    await dqTriggerDawnRender();

  } catch (err) {
    if (err.name === 'AbortError') return;
    delete _drawerControllers[key];
    dqDrawerShowError(key, 'Something went wrong. Please try again.');
    allBtns.forEach(el => el.removeAttribute('disabled'));
  }
}

function dqDrawerShowError(key, message) {
  // Find the item and inject or update error element
  const btn = document.querySelector(`[data-key="${key}"]`);
  const item = btn?.closest('.dq-cart-item');
  if (!item) return;
  let el = item.querySelector('.dq-qty-error');
  if (!el) {
    el = document.createElement('p');
    el.className = 'dq-qty-error';
    item.querySelector('.dq-cart-item__details')?.appendChild(el);
  }
  el.textContent = message;
  setTimeout(() => el.remove(), 5000);
}

function dqInitDrawerQtyDelegation() {
  const drawer = document.getElementById('CartDrawer');
  if (!drawer || drawer.dataset.dqQtyBound) return;
  drawer.dataset.dqQtyBound = '1';

  drawer.addEventListener('click', (e) => {
    const minus = e.target.closest('.dq-drawer-minus');
    if (minus) {
      const key = minus.dataset.key;
      const span = drawer.querySelector(`.dq-cart-item__qty-value[data-key="${key}"]`);
      const current = span ? parseInt(span.textContent, 10) : 1;
      dqDrawerUpdateQty(key, Math.max(0, current - 1));
      return;
    }

    const plus = e.target.closest('.dq-drawer-plus');
    if (plus) {
      const key = plus.dataset.key;
      const span = drawer.querySelector(`.dq-cart-item__qty-value[data-key="${key}"]`);
      const current = span ? parseInt(span.textContent, 10) : 0;
      dqDrawerUpdateQty(key, current + 1);
      return;
    }

    const remove = e.target.closest('.dq-drawer-remove');
    if (remove) {
      dqDrawerUpdateQty(remove.dataset.key, 0);
      return;
    }
  });
}

/* ─── Expandables ────────────────────────────────────────────── */

function dqInitExpandables() {
  dqAll('.dq-cart-expandable__trigger:not([data-dq-bound])').forEach((trigger) => {
    trigger.dataset.dqBound = '1';
    trigger.addEventListener('click', () => {
      const isOpen = trigger.getAttribute('aria-expanded') === 'true';
      trigger.setAttribute('aria-expanded', String(!isOpen));
      trigger.nextElementSibling?.classList.toggle('is-open', !isOpen);
    });
  });
}

/* ─── Called directly by Dawn after every renderContents ─────── */

function dqOnRenderComplete() {
  const cartDrawerEl = document.querySelector('cart-drawer');
  const hasItems     = !!document.querySelector('#CartDrawer-CartItems .dq-cart-item');
  if (cartDrawerEl && hasItems) {
    cartDrawerEl.classList.remove('dq-cart-drawer--empty', 'is-empty');
  }
  dqInitUpsell();
  dqInitExpandables();
  dqSyncCartCount();
}

/* ─── Observer ───────────────────────────────────────────────── */
// Fires after Dawn replaces #CartDrawer innerHTML.
// Re-binds JS interactions on fresh nodes and removes stale empty-state class.

let _observerTimer = null;

function dqOnDrawerMutated() {
  clearTimeout(_observerTimer);
  _observerTimer = setTimeout(dqOnRenderComplete, 100);
}

/* ─── Bootstrap ──────────────────────────────────────────────── */

function dqBootstrap() {
  const cartDrawer = document.getElementById('CartDrawer');
  if (cartDrawer) {
     new MutationObserver(dqOnDrawerMutated).observe(cartDrawer, {
      childList: true,
    });
  }

  if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartError, () => dqTriggerDawnRender());
  }

  dqInitDrawerQtyDelegation();
  dqInitUpsell();
  dqInitExpandables();
  dqSyncCartCount();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', dqBootstrap);
} else {
  dqBootstrap();
}